'use client'

import { getI18n, I18nDictionary, LangCode, useI18nValue } from '@/components/i18n/lang-context'
import { SectionTitle } from '@/components/jbda/section-title'
import { Nav } from '@/components/layout/nav'
import { NavItem } from '@/components/layout/nav-item'
import { classifyPartners } from '@/components/partners/partner'
import { PartnerCard, showMore } from '@/components/partners/partner-card'
import { isPartnerFiltered_clientSide, stopPartnerFiltering_clientSide } from '@/components/partners/partner-inbound-link'
import PartnersMap from '@/components/partners/partners-map'
import { Button } from '@/components/ui/button'
import { Flex } from '@/components/ui/flex'
import { cn } from '@/lib/utils/utils'
import { useMounted } from '@/lib/utils/utils-client'
import { Address, AREAS } from '@/types/partner'
import { useEffect, useState } from 'react'
import { FaMapMarkedAlt } from 'react-icons/fa'
import { MdKeyboardArrowDown } from 'react-icons/md'
import { FINANCE_PARTNERS_MAP } from './finance-partners'
import { REAL_ESTATE_PARTNERS_MAP } from './real-estate-partners'

const I18N: I18nDictionary = {
  hero_title: {
    en: 'They *trust* Legibloq®',
    fr: 'Ils font *confiance* à Legibloq®'
  },
  hero: {
    en: '***Pioneering*** yet ***discerning***, these professionals ***trust*** us to guide them toward a ***well-managed adoption*** of cryptocurrencies.',
    fr: '***Pionniers*** mais ***exigeants***, ces professionnels nous font ***confiance*** pour les accompagner vers une ***adoption maîtrisée*** des cryptomonnaies.'
  },
  welcome: {
    en: 'Welcome on behalf of {partner}, our valued partner',
    fr: 'Bienvenue de la part de {partner}, notre partenaire de confiance'
  },
  ourPartner: {
    en: 'Our Partners',
    fr: 'Nos Partenaires'
  },
  realEstateTitle: {
    en: 'Real Estate',
    fr: 'Immobilier'
  },
  financeTitle: {
    en: 'Finance',
    fr: 'Finance'
  },
  countryChoice: {
    en: 'Country',
    fr: 'Pays'
  },
  areaChoice: {
    en: 'Region',
    fr: 'Département'
  },
  cityChoice: {
    en: 'City',
    fr: 'Ville'
  },
  removeFilter: {
    en: 'Remove Filter',
    fr: 'Retirer le filtre'
  },
  showMap: {
    en: 'Show Map',
    fr: 'Afficher la carte'
  },
  hideMap: {
    en: 'Hide Map',
    fr: 'Cacher la carte'
  }
}

export default function Partners({ langCode, partnerId, orderedRealEstatePartnersId, classifiedRealEstatePartners, orderedFinancePartnersId, classifiedFinancePartners }: {
  langCode: LangCode, partnerId: string|undefined,
  orderedRealEstatePartnersId: string[], classifiedRealEstatePartners: { [countryCode: string]: {[area: string] : {[city: string] : {[partnerId: string]: Address[]}}}},
  orderedFinancePartnersId: string[], classifiedFinancePartners: { [countryCode: string]: {[area: string] : {[city: string] : {[partnerId: string]: Address[]}}}}
  }) {
  const i18n = getI18n(I18N, langCode)
  const i18nValue = useI18nValue()

  const [realEstateOpened, setRealEstateOpened] = useState<boolean>(true)
  // const [financeOpened, setFinanceOpened] = useState<boolean>(true)
  const [mapOpened, setMapOpened] = useState<boolean>(true)

  // const [realEstateUnderlined, setRealEstateUnderlined] = useState<boolean>(false)
  // const [financeUnderlined, setFinanceUnderlined] = useState<boolean>(false)

  const [selectedCountryCode, setSelectedCountryCode] = useState<string>()
  const [selectedArea, setSelectedArea] = useState<string>()
  const [selectedCity, setSelectedCity] = useState<string>()

  // Have to be initialized in order for the partners to be loaded and displayed in case javascript is disabled
  const [realEstatePartnersId, setRealEstatePartnersId] = useState<string[]>(orderedRealEstatePartnersId)
  const [partnerOnly, setPartnerOnly] = useState<boolean>()

  const [navItems, setNavItems] = useState<NavItem[]>([])

  const partner = partnerId ? REAL_ESTATE_PARTNERS_MAP[partnerId] || FINANCE_PARTNERS_MAP[partnerId] : undefined
  let partnerMessage: string | undefined = partner ? i18n.welcome.replace('{partner}', '*' + partner.name + '*') : undefined
  const otherPartners = orderedRealEstatePartnersId.filter((id) => partnerId !== id).map((id) => REAL_ESTATE_PARTNERS_MAP[id])

  const getClassifiedRealEstatePartners = () => {
    if(partner && partnerOnly) {
      const classifiedPartners: { [countryCode: string]: {[area: string] : {[city: string] : {[partnerId: string]: Address[]}}}} = {}
      classifyPartners([partner], partnerId, partnerOnly, [], classifiedPartners)
      return classifiedPartners
    }
    return classifiedRealEstatePartners
  }

  const getPartnersId = (countryCode: string|undefined, area: string|undefined, city: string|undefined,
    partners: { [countryCode: string]: {[area: string] : {[city: string] : {[partnerId: string]: Address[]}}}}) => {
    const result: string[] = []
    Object.keys(partners).forEach(_countryCode => {
      if(!!countryCode && countryCode !==_countryCode)
        return
      Object.keys(partners[_countryCode]).forEach(_area => {
        if(!!area && area !==_area)
          return
        Object.keys(partners[_countryCode][_area]).forEach(_city => {
          if(!!city && city !==_city)
            return
          result.push(...Object.keys(partners[_countryCode][_area][_city]))
        })
      })
    })
    return result
  }
  const setFilters = (countryCode: string|undefined, area: string|undefined, city: string|undefined) => {
    const _classifiedRealEstatePartners = getClassifiedRealEstatePartners()
    const navItems: NavItem[] = []
    // Don't show country as long as only French presence
    let navItem: NavItem //= {title: countryCode ? i18nValue(COUNTRIES[countryCode], langCode) : I18N['countryChoice'], subs: []}
    let subs: NavItem[]
    // navItems.push(navItem)
    // subs = []
    // Object.keys(_classifiedRealEstatePartners).forEach(countryCode => {
    //   subs.push({title: COUNTRIES[countryCode], onClick: () => selectCountry(countryCode), iconName: 'logo'})
    // });
    // subs = subs.sort((a, b) => (a.title as string).localeCompare(b.title as string))
    //  if(area) {
    //    subs = [{title: i18n.removeFilter, onClick: () => selectCountry(undefined), iconName: 'logo'}, ...subs]
    //  }
    //  navItem.subs = subs
    if(countryCode) {
      navItem = {title: area ? i18nValue(AREAS[area], langCode) : I18N['areaChoice']}
      navItems.push(navItem)
      subs = []
      Object.keys(_classifiedRealEstatePartners[countryCode]).forEach(area => {
        subs.push({title: i18nValue(AREAS[area], langCode), onClick: () => selectArea(area), iconName: 'logo'})
      })
      subs = subs.sort((a, b) => (a.title as string).localeCompare(b.title as string))
      if(area) {
        subs = [{title: i18n.removeFilter, onClick: () => selectArea(undefined), iconName: 'logo'}, ...subs]
      }
      navItem.subs = subs
      if(area) {
        navItem = {title: city ? city : I18N['cityChoice']}
        navItems.push(navItem)
        subs = []
        Object.keys(_classifiedRealEstatePartners[countryCode][area]).forEach(city => {
          subs.push({title: city, onClick: () => selectCity(city), iconName: 'logo'})
        })
        subs = subs.sort((a, b) => (a.title as string).localeCompare(b.title as string))
        if(city) {
          subs = [{title: i18n.removeFilter, onClick: () => selectCity(undefined), iconName: 'logo'}, ...subs]
        }
        navItem.subs = subs
      }
    }
    setNavItems(navItems)
  }

  const mounted = useMounted()
  useEffect(() => {
    if(!mounted) return
    openRealEstate()
    const partnerOnly = !!partner && isPartnerFiltered_clientSide()
    setPartnerOnly(partnerOnly)
    showMap(partnerOnly)
  }, [mounted])

  const selectCountry = (code: string|undefined) => {
    setSelectedCountryCode(code)
  }
  const selectArea = (area: string|undefined) => {
    setSelectedArea(area)
  }
  const selectCity = (area: string|undefined) => {
    setSelectedCity(area)
  }
  useEffect(() => {
    const _classifiedRealEstatePartners = getClassifiedRealEstatePartners()
    if(!selectedCountryCode && Object.keys(_classifiedRealEstatePartners).length == 1) {
      selectCountry(Object.keys(_classifiedRealEstatePartners)[0])
      return
    }
    if(selectedCountryCode && Object.keys(_classifiedRealEstatePartners[selectedCountryCode]).length == 1) {
      selectArea(Object.keys(_classifiedRealEstatePartners[selectedCountryCode])[0])
      return
    }
    selectArea(undefined)
    selectCity(undefined)
    setFilters(selectedCountryCode, undefined, undefined)
    const realEstatePartnersId: string[] = getPartnersId(selectedCountryCode, undefined, undefined, _classifiedRealEstatePartners)
    setRealEstatePartnersId(orderedRealEstatePartnersId.filter(_id => realEstatePartnersId.includes(_id)))
  }, [selectedCountryCode])

  useEffect(() => {
    const _classifiedRealEstatePartners = getClassifiedRealEstatePartners()
    if(selectedCountryCode && selectedArea && Object.keys(_classifiedRealEstatePartners[selectedCountryCode][selectedArea]).length == 1) {
      selectCity(Object.keys(_classifiedRealEstatePartners[selectedCountryCode][selectedArea])[0])
      return
    }
    selectCity(undefined)
    setFilters(selectedCountryCode, selectedArea, undefined)
    const realEstatePartnersId: string[] = getPartnersId(selectedCountryCode, selectedArea, undefined, _classifiedRealEstatePartners)
    setRealEstatePartnersId(orderedRealEstatePartnersId.filter(_id => realEstatePartnersId.includes(_id)))
  }, [selectedArea])
  
  useEffect(() => {
    const _classifiedRealEstatePartners = getClassifiedRealEstatePartners()
    setFilters(selectedCountryCode, selectedArea, selectedCity)
    const realEstatePartnersId: string[] = getPartnersId(selectedCountryCode, selectedArea, selectedCity, _classifiedRealEstatePartners)
    setRealEstatePartnersId(orderedRealEstatePartnersId.filter(_id => realEstatePartnersId.includes(_id)))
  }, [selectedCity])

  useEffect(() => {
    if(partnerOnly === undefined) return
    if(partnerOnly && partnerId && partner) {
      setRealEstatePartnersId([partnerId])
    }
    else {
      stopPartnerFiltering_clientSide()
      setRealEstatePartnersId(orderedRealEstatePartnersId)
    }
    selectCountry(undefined)
  }, [partnerOnly])

  const openRealEstate = () => {
    setRealEstateOpened(true)
    // setRealEstateUnderlined(true)
    // setFinanceOpened(false)
    // setFinanceUnderlined(false)
  }
  // const openFinance = () => {
  //   setRealEstateOpened(false)
  //   setRealEstateUnderlined(false)
  //   setFinanceOpened(true)
  //   setFinanceUnderlined(true)
  // }

  // const handleMouseOverAndOut = (type: 'realEstate'|'finance', over: boolean) => {
  //   if(type === 'realEstate') {
  //     if(realEstateOpened) return
  //     setRealEstateUnderlined(over)
  //   }
  //   if(type === 'finance') {
  //     if(financeOpened) return
  //     setFinanceUnderlined(over)
  //   }
  // }
  // const handleMouseOverRealEstate = () => {
  //   handleMouseOverAndOut('realEstate', true)
  // }
  // const handleMouseOutRealEstate = () => {
  //   handleMouseOverAndOut('realEstate', false)
  // }
  // const handleMouseOverFinance = () => {
  //   handleMouseOverAndOut('finance', true)
  // }
  // const handleMouseOutFinance = () => {
  //   handleMouseOverAndOut('finance', false)
  // }

  const showMap = (show: boolean) => {
    setMapOpened(show)
  }

  return <>
    <div id='INTRO_SECTION' className='relative w-full overflow-hidden'>
      <div className='relative z-10 m-auto w-full p-4 pb-12 pt-24 sm:pt-48'>
        <SectionTitle title={i18n.hero_title} desc={i18n.hero} langCode={langCode} titleMaxWidth='max-w-xl' titleText='text-4xl md:text-5xl' descText='text-lg' descMaxWidth='max-w-2xl' mainTitle={true} />
      </div>
    </div>
    {partner && (
      <div className='bg-background2'>
        <section id='WELCOME' className='relative z-10 px-4 pt-2'>
          <SectionTitle title={partnerMessage} langCode={langCode} titleMaxWidth='max-w-2xl' />
        </section>
        <section id='PARTNER' className='px-4 py-8 pb-12'>
          <div className='mx-auto max-w-4xl'>
            <PartnerCard partner={partner} langCode={langCode} highlighted={true} />
          </div>
        </section>
      </div>
    )}
    <div className='relative z-10'>
      <section id='PARTNERS' className='z-10 px-4 py-6 opacity-70'>
        <SectionTitle title={i18n.ourPartner} langCode={langCode} titleMaxWidth='max-w-2xl' />
      </section>

      {/* <section id='PARTNER_TYPES' className={cn(realEstateOpened && financeOpened ? 'hidden' : '')}>
        <div className='mx-auto max-w-7xl'>
          <div className='mb-8 flex flex-wrap justify-center'>
            <div className={cn(realEstateUnderlined ? 'underline' : '',
              realEstateOpened ? 'text-primary dark:text-primaryBright' : 'cursor-pointer text-gray-900 dark:text-white',
              ' w-1/2 pr-2 text-right text-2xl font-bold md:pr-6 md:text-3xl')}
            onMouseOver={handleMouseOverRealEstate} onMouseOut={handleMouseOutRealEstate}
            onClick={openRealEstate}>
              {i18n.realEstateTitle}
            </div>
            <div className={cn(financeUnderlined ? 'underline' : '',
              financeOpened ? 'text-primary dark:text-primaryBright' : 'cursor-pointer text-gray-900 dark:text-white',
              ' w-1/2 pl-2 text-left text-2xl font-bold md:pl-6 md:text-3xl')}
            onMouseOver={handleMouseOverFinance} onMouseOut={handleMouseOutFinance}
            onClick={openFinance}>
              {i18n.financeTitle}
            </div>
          </div>
        </div>
      </section> */}

      <section id='PARTNER_MAP' className={realEstateOpened ? '' : 'hidden'}>
        <div className='mx-auto max-w-4xl'>
          <Flex className='row justify-center items-center'>
            <Nav navItems={navItems} langCode={langCode} className='justify-center text-2xl' />
            <Flex className='cursor-pointer items-center gap-0 text-sm transition-all' onClick={() => showMap(!mapOpened)}>
              <Button variant={'link'} className={cn('text-[14px] font-medium text-foreground opacity-75')}>
                {mapOpened ? i18n.hideMap : i18n.showMap}
                <div id='ICON'><FaMapMarkedAlt size={20}/></div>
                <div id='ARROW' className='text-lg font-semibold opacity-70'>
                  <MdKeyboardArrowDown size={16} className={cn('transition-all', mapOpened ? '' : ' rotate-[270deg]')} />
                </div>
              </Button>
            </Flex>
          </Flex>
          <div  className={cn('rounded-2xl overflow-hidden', mapOpened ? '' : 'hidden')} >
            <PartnersMap shown={mapOpened} mapPartners={REAL_ESTATE_PARTNERS_MAP} partnerId={partnerId} partnerOnly={!!partnerOnly}
                         countryCode={selectedCountryCode} area={selectedArea} city={selectedCity} langCode={langCode} />
          </div>
        </div>
      </section>
      {/* Real Estate Partners Section */}
      <section id='REAL_ESTATE_PARTNERS' className={cn(realEstateOpened ? '' : 'hidden')}>
        <div className='mx-auto max-w-7xl'>
          {/* <div className={cn(realEstateOpened && financeOpened ? '' : 'hidden', 'mb-8 text-center')}>
            <h2 className='text-2xl font-bold text-gray-900 dark:text-white md:text-3xl'>{i18n.realEstateTitle}</h2>
          </div> */}
          <div className='flex flex-wrap justify-center gap-8 px-4 py-6 pb-20'>
            {realEstatePartnersId.map((id) => {
              const partner = REAL_ESTATE_PARTNERS_MAP[id]
              return (<div key={partner.name} className='w-full sm:w-[350px] lg:w-1/3'>
                <PartnerCard partner={partner} langCode={langCode} />
              </div>)
            })}
            {!partnerOnly ?
            <></> :
            <div key={'more partner'} className='w-full sm:w-[350px] lg:w-1/3 mt-[150px] cursor-pointer' onClick={()=>setPartnerOnly(false)}>
              {showMore(otherPartners, langCode, false)}
            </div>}
          </div>
        </div>
      </section>
      {/* Finance Partners Section */}
      {/* <section id='FINANCE_PARTNERS' className={cn(financeOpened ? '' : 'hidden')}>
        <div className='mx-auto max-w-7xl'>
          <div className={cn(realEstateOpened && financeOpened ? '' : 'hidden', 'mb-8 text-center')}>
            <h2 className='text-2xl font-bold text-gray-900 dark:text-white md:text-3xl'>{i18n.financeTitle}</h2>
          </div>
          <div className='flex flex-wrap justify-center gap-8 px-4 py-6 pb-20'>
            {orderedFinancePartnersId.map((id) => {
              const partner = mapFinancePartners[id]
              return (<div key={partner.name} className='w-full sm:w-[350px] lg:w-1/3'>
                <PartnerCard partner={partner} langCode={langCode} />
              </div>)
            })}
          </div>
        </div>
      </section> */}
    </div>
  </>
}